import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import FileUploader from '@/components/FileUploader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, FileSpreadsheet, CheckCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkspaceId } from '@/contexts/WorkspaceContext';
import { useSubscriptionGuard } from '@/hooks/useSubscriptionGuard';
import { authFetch } from '@/lib/api';

export default function Upload() {
  const { t } = useTranslation();
  const workspaceId = useWorkspaceId();
  const { guard, nudge } = useSubscriptionGuard('Importing contacts requires an active subscription.');
  const [uploading, setUploading] = useState(false);
  const [lastUpload, setLastUpload] = useState<{
    count: number;
    message: string;
    duplicatesSkipped: number;
    invalidPhones: number;
    globallyOptedOut: number;
    unknownCountries: number;
  } | null>(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await authFetch(`/workspaces/${workspaceId}/uploads/template`);

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contacts_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
      console.error(error);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setLastUpload(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      // authFetch rather than apiFetch: this reads the raw envelope itself, below.
      const response = await authFetch(`/workspaces/${workspaceId}/uploads`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      const data = await response.json();

      if (data.data.contactsImported !== undefined) {
        setLastUpload({
          count: data.data.contactsImported,
          message: data.data.message,
          duplicatesSkipped: data.data.duplicatesSkipped ?? 0,
          invalidPhones: data.data.invalidPhones ?? 0,
          globallyOptedOut: data.data.globallyOptedOut ?? 0,
          unknownCountries: data.data.unknownCountries ?? 0,
        });
        // The server's message already accounts for rows that were skipped, which is the whole
        // point: re-importing a file that adds nothing has to say so rather than claim success.
        toast.success(data.data.message);
      } else {
        toast.success('File uploaded successfully');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />

      <main className="flex-1 p-4 md:p-8">
        <div className="container mx-auto max-w-4xl">
          <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2">
            <Link to={`/w/${workspaceId}/contacts`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Contacts
            </Link>
          </Button>

          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2">{t('upload.title')}</h1>
              <p className="text-muted-foreground">{t('upload.subtitle')}</p>
            </div>

            <Button variant="outline" onClick={guard(handleDownloadTemplate)} className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Download Template
            </Button>
          </div>

          <div className="grid gap-6">
            <Card className="p-8">
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Bulk Import Contacts</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload an Excel or CSV file to import contacts. Use the template to ensure correct formatting.
                </p>
                <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-3 rounded-md border border-blue-100">
                  <FileSpreadsheet className="h-4 w-4" />
                  <span>Supported formats: .xlsx, .xls, .csv</span>
                </div>
              </div>

              <FileUploader onUpload={guard(handleUpload)} />

              {uploading && (
                <div className="mt-4 text-center text-sm text-muted-foreground animate-pulse">
                  Importing contacts...
                </div>
              )}
            </Card>

            {lastUpload && (
              <Card className="p-6 bg-green-50 border-green-100">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 shrink-0 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    <CheckCircle className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-900">
                      {lastUpload.count > 0 ? 'Import Successful' : 'Nothing New to Import'}
                    </h3>
                    <p className="text-green-700">{lastUpload.message}</p>
                    {lastUpload.duplicatesSkipped > 0 && (
                      <p className="text-sm text-green-700/80 mt-1">
                        Duplicates are matched on phone number, so the same person is never added twice.
                      </p>
                    )}
                    {lastUpload.invalidPhones > 0 && (
                      <p className="text-sm text-green-700/80 mt-1">
                        Invalid numbers need a country code, for example +971501234567.
                      </p>
                    )}
                    {lastUpload.globallyOptedOut > 0 && (
                      <p className="text-sm text-green-700/80 mt-1">
                        Numbers that opted out of all messages on the platform are added as unsubscribed
                        and will never be messaged.
                      </p>
                    )}
                    {lastUpload.unknownCountries > 0 && (
                      <p className="text-sm text-green-700/80 mt-1">
                        Current Country takes a two-letter code (AE, SA, EG) or the country&apos;s name in
                        English or Arabic.
                      </p>
                    )}
                  </div>
                  {lastUpload.count > 0 && (
                    <Button variant="outline" asChild>
                      <Link to={`/w/${workspaceId}/contacts`}>View contacts</Link>
                    </Button>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
        {nudge}
      </main>
    </div>
  );
}
